import React, { useCallback, useEffect, useState, Suspense } from 'react';
import classes from './App.module.css';
import { Switch, Route, Redirect, useLocation } from 'react-router';
import Login from './components/Login/Login';
import { useStore } from './store/store';
import useV3Axios from './hooks/useV3Axios';
import { HD_LOGIN, LOGOUT } from './store/actionTypes';
import { LOCAL_AUTH_JWT, LOCAL_LANG } from './utils/constants';
import { useEffectOnce } from 'react-use';
import CenteredCustomSpinner from './ui/CenteredCustomSpinner';
import Layout from './components/Navigation/Layout/Layout';
import { IntlProvider } from 'react-intl';
import English from './lang/en.json';
import Hindi from './lang/hi.json';
import Kannada from './lang/kn.json';
import Malayalam from './lang/ml.json';
import Marathi from './lang/mr.json';
import Tamil from './lang/ta.json';
import Telugu from './lang/te.json';
import {
	ARCHIVES_PATH, LOGIN_PATH, LOGOUT_PATH, MCP_CARD_PATH, PARTOGRAM_PATH,
	TEST_PATH, TEST_VIEW_ALL_ITEM_PATH, TEST_VIEW_ALL_PATH, TEST_VIEW_PATH,
	ARCHIVES_VIEW_PATH, TEST_VIEW_ITEM_MINI_PATH, HOME_PATH,
	REGISTER_PATH, REGISTER_VERIFY_PATH, REGISTER_COMPLETE_PATH, REGISTER_INVITE_PATH,
	FORGOT_PASSWORD_PATH, RESET_PASSWORD_PATH, ADMIN_PATH,
} from './utils/paths';
import Register from './components/Register/Register';
import RegisterVerify from './components/Register/RegisterVerify';
import RegisterComplete from './components/Register/RegisterComplete';
import RegisterInvite from './components/Register/RegisterInvite';
import ForgotPassword from './components/ForgotPassword/ForgotPassword';
import ResetPassword from './components/ResetPassword/ResetPassword';
import { DashboardProvider } from './contexts/DashboardContext';
import { SettingsProvider } from './contexts/SettingsContext';

const Logout = React.lazy(() => import('./components/Logout/Logout'));
const HomePage = React.lazy(() => import('./components/HomePage/HomePage'));

const App = () => {
	const [state, dispatch] = useStore();
	const [loading, setLoading] = useState(true);

	const { data: refreshData, error: refreshError, sendRequest: sendRefresh } = useV3Axios();

	const location = useLocation();

	let locale = localStorage.getItem(LOCAL_LANG);
	let lang = English;
	if (locale === 'hi') lang = Hindi;
	else if (locale === 'kn') lang = Kannada;
	else if (locale === 'ml') lang = Malayalam;
	else if (locale === 'mr') lang = Marathi;
	else if (locale === 'ta') lang = Tamil;
	else if (locale === 'te') lang = Telugu;

	// Auto-signin: attempt token refresh on page load
	useEffectOnce(() => {
		if (localStorage.getItem(LOCAL_AUTH_JWT)) {
			sendRefresh('/auth/refresh', {});
		} else {
			setLoading(false);
		}
	});

	useEffect(useCallback(() => {
		if (refreshData) {
			dispatch(HD_LOGIN, { data: refreshData });
			const currentSeconds = new Date().getTime() / 1000;
			const timeout = ((parseInt(refreshData.expiresAt) - parseInt(currentSeconds)) * 1000);
			setTimeout(() => { dispatch(LOGOUT, { pathName: location.pathname }); }, timeout);
			setLoading(false);
		}
	}, [refreshData, dispatch, location]), [refreshData]);

	useEffect(useCallback(() => {
		// Refresh failed (expired / invalid token) — show login
		if (refreshError) setLoading(false);
	}, [refreshError]), [refreshError]);

	let content = null;

	if (loading) {
		content = <CenteredCustomSpinner />;
	} else {
		if (state.authData && state.authData.email) {
			content = (
				<Layout>
					<SettingsProvider>
						<DashboardProvider>
							<Suspense fallback={<p>Loading...</p>}>
								<Switch>
									<Route exact path={HOME_PATH} render={(props) => <HomePage {...props} />} />
									<Route exact path={TEST_PATH} render={(props) => <HomePage {...props} />} />
									<Route exact path={PARTOGRAM_PATH} render={(props) => <HomePage {...props} />} />
									<Route exact path={MCP_CARD_PATH} render={(props) => <HomePage {...props} />} />
									<Route exact path={ARCHIVES_PATH} render={(props) => <HomePage {...props} />} />
									<Route exact path={TEST_VIEW_ALL_PATH} render={(props) => <HomePage {...props} />} />
									<Route path={`${TEST_VIEW_PATH}/:id`} render={(props) => <HomePage {...props} />} />
									<Route path={`${TEST_VIEW_ITEM_MINI_PATH}/:id`} render={(props) => <HomePage {...props} />} />
									<Route path={`${TEST_VIEW_ALL_ITEM_PATH}/:id`} render={(props) => <HomePage {...props} />} />
									<Route path={`${MCP_CARD_PATH}/:id`} render={(props) => <HomePage {...props} />} />
									<Route path={`${ARCHIVES_VIEW_PATH}/:id`} render={(props) => <HomePage {...props} />} />
									<Route path={`${PARTOGRAM_PATH}/:id`} render={(props) => <HomePage {...props} />} />
									{state.authData.role === 'admin' && (
										<Route exact path={ADMIN_PATH} render={(props) => <HomePage {...props} />} />
									)}
									<Route exact path={LOGOUT_PATH} render={(props) => <Logout {...props} />} />
									{/* <Route exact path={STATS_PATH} render={(props) => <HomePage {...props} />} /> */}
									{state.authData.redirectPath ? <Redirect to={state.authData.redirectPath} /> : <Redirect to={HOME_PATH} />}
								</Switch>
							</Suspense>
						</DashboardProvider>
					</SettingsProvider>
				</Layout>
			);
		} else {
			content = (
				<Switch>
					<Route path={LOGIN_PATH} render={(props) => <Login {...props} />} />
					<Route exact path={REGISTER_PATH} render={(props) => <Register {...props} />} />
					<Route exact path={REGISTER_VERIFY_PATH} render={(props) => <RegisterVerify {...props} />} />
					<Route exact path={REGISTER_COMPLETE_PATH} render={(props) => <RegisterComplete {...props} />} />
					<Route path={REGISTER_INVITE_PATH} render={(props) => <RegisterInvite {...props} />} />
					<Route exact path={FORGOT_PASSWORD_PATH} render={(props) => <ForgotPassword {...props} />} />
					<Route exact path={RESET_PASSWORD_PATH} render={(props) => <ResetPassword {...props} />} />
					<Redirect to={LOGIN_PATH} />
				</Switch>
			);
		}
	}

	return (
		<IntlProvider locale={locale} messages={lang}>
			<div className={classes.App}>
				{content}
			</div>
		</IntlProvider>
	);
};

export default App;
